/*
 * @Author: Ryan
 * @Date: 2026-02-06
 * @Description: 视频翻译 Landing 页面 - Japanese to English
 */
import type { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import React from 'react';

import { withLandingLayout } from '@/landing/components/LandingLayout';
import SEOHead, { type SEOHeadProps } from '@/landing/components/SEOHead';
import type { FAQItem } from '@/landing/components/templates/FAQSection';
import type { FeatureItem } from '@/landing/components/templates/FeaturesSection';
import HeroSection from '@/landing/components/templates/HeroSection';
import type { StepData } from '@/landing/components/templates/HowToSection';
import type { UseCaseItem } from '@/landing/components/templates/UseCasesSection';
import type { ReasonItem } from '@/landing/components/templates/WhyChooseSection';
import {
  createBreadcrumbSchema,
  createFAQSchema,
  createOrganizationSchema,
  createSoftwareAppSchema,
} from '@/landing/utils/structuredData';

// 非首屏组件懒加载（代码拆分，减少首屏 JS 体积）
const VideoTranslationDemos = dynamic(
  () => import('@/landing/components/templates/VideoTranslationDemos'),
);
const FeaturesSection = dynamic(
  () => import('@/landing/components/templates/FeaturesSection'),
);
const HowToSection = dynamic(() => import('@/landing/components/templates/HowToSection'));
const UseCasesSection = dynamic(
  () => import('@/landing/components/templates/UseCasesSection'),
);
const LanguageListSection = dynamic(
  () => import('@/landing/components/templates/LanguageListSection'),
);
const WhyChooseSection = dynamic(
  () => import('@/landing/components/templates/WhyChooseSection'),
);
const VsSection = dynamic(() => import('@/landing/components/templates/VsSection'));
const ExploreToolsSection = dynamic(
  () => import('@/landing/components/templates/ExploreToolsSection'),
);
const FAQSection = dynamic(() => import('@/landing/components/templates/FAQSection'));
const FooterCTASection = dynamic(
  () => import('@/landing/components/templates/FooterCTASection'),
);

// ============ Static Content - Japanese to English ============

const PAGE_URL = 'https://falcocut.ai/translate-video/japanese-to-english';

// Hero Section
const HERO_CONTENT = {
  title: 'Translate Japanese Video to English Online with AI',
  subtitle:
    'Upload your video and let FalcoCut translate Japanese video to English in minutes. It auto-translates voice and subtitles, adds lip sync, and lets you show bilingual subtitles or translated-only subtitles with easy style controls.',
  cta: 'Upload File',
  sourceLanguage: 'Japanese',
  targetLanguage: 'English',
};

// Video Translation Demos
const DEMOS_CONTENT = {
  title: 'See a Japanese Video Turn Into Clear English in Seconds',
  subtitle:
    'Watch FalcoCut show a side-by-side preview from the original Japanese video to the English version. You get clean subtitles, natural voice options, and a result that is easy to follow.',
  cta: 'Translate Japanese Video',
};

// Features Section
const FEATURES_CONTENT = {
  title: 'Unlock English Reach From Japanese Videos Fast',
  subtitle:
    'FalcoCut helps you turn Japanese videos into clear English versions that feel ready to share. You get AI dubbing, subtitles, and lip sync in one place, with simple controls for beginners.',
  cta: 'Translate Japanese Video',
};

const FEATURE_IMAGES = [
  'https://assets.falcocut.ai/tools/video-translator/translate-into-different-languages-instantly.webp',
  'https://assets.falcocut.ai/tools/video-translator/extensive-library-of-authentic-ai-voices.webp',
  'https://assets.falcocut.ai/tools/video-translator/ai-voice-cloning-and-natural-dubbing.webp',
  'https://assets.falcocut.ai/tools/video-translator/accurate-subtitles-with-editing-options.webp',
  'https://assets.falcocut.ai/tools/video-translator/seamless-lip-sync-for-realism.webp',
  'https://assets.falcocut.ai/tools/video-translator/all-in-one-video-localization-platform.webp',
];

const FEATURES: FeatureItem[] = [
  {
    title: 'More Languages for Every Audience You Want',
    description:
      'Reach new viewers by making English versions from Japanese content in a few clicks. FalcoCut supports 35 languages, so you can also create extra versions for other regions. This helps you reuse one video across many channels without extra filming. Subtitles and voice can match the same target language for a clean, consistent result.',
    imageUrl: FEATURE_IMAGES[0],
    imageAlt: 'More Languages for Every Audience You Want',
  },
  {
    title: 'Natural AI Voices That Sound Human',
    description:
      'Pick a voice that fits your video, so the English dub feels believable and easy to follow. FalcoCut offers many realistic voice options with different ages, tones, and styles. This helps you avoid a flat, robotic sound that makes viewers leave early. A better voice keeps attention and makes your message feel more trustworthy.',
    imageUrl: FEATURE_IMAGES[1],
    imageAlt: 'Natural AI Voices That Sound Human',
  },
  {
    title: 'Your Brand Voice Stays Consistent Across Dubs',
    description:
      'Keep a familiar sound even when you post in English for a new audience. FalcoCut supports AI dubbing and voice cloning for a more consistent creator identity. This is helpful for series content, product demos, and channel intros that repeat often. Viewers recognize the voice faster, which can improve trust and return visits.',
    imageUrl: FEATURE_IMAGES[2],
    imageAlt: 'Your Brand Voice Stays Consistent Across Dubs',
  },
  {
    title: 'Accurate Subtitles You Can Clean Up Quickly',
    description:
      'Get English subtitles from Japanese speech, plus help with on-screen text when it matters. FalcoCut creates subtitles fast, then lets you edit timing and wording for clarity. This is great when names, slang, or brand terms need a quick fix. Clean subtitles improve watch time, even when viewers keep sound off.',
    imageUrl: FEATURE_IMAGES[3],
    imageAlt: 'Accurate Subtitles You Can Clean Up Quickly',
  },
  {
    title: 'Lip Sync That Looks Smooth on Talking Scenes',
    description:
      'Make the English voice feel like it belongs to the person on screen. FalcoCut lip sync matches mouth movement to the new audio for a more natural look. This matters most for interviews, presenters, and face-to-camera clips where mismatch feels distracting. Better sync helps viewers focus on the story instead of the edit.',
    imageUrl: FEATURE_IMAGES[4],
    imageAlt: 'Lip Sync That Looks Smooth on Talking Scenes',
  },
  {
    title: 'One Workspace for Subtitles, Dubbing, and Export',
    description:
      'Finish your full localization without juggling extra tools or messy handoffs. FalcoCut brings subtitles, voice, lip sync, and styling into one workflow. This saves time and reduces errors that happen when files move between apps. You get a polished English-ready video that is easier to publish on any platform.',
    imageUrl: FEATURE_IMAGES[5],
    imageAlt: 'One Workspace for Subtitles, Dubbing, and Export',
  },
];

// How To Section
const HOW_TO_CONTENT = {
  title: 'How to Translate Japanese Video to English in FalcoCut',
  cta: 'Start Translating',
};

const HOW_TO_STEPS: StepData[] = [
  {
    number: 1,
    title: 'Step 1: Upload Your Video',
    description:
      'Click Add Video to Translate, or pick one from Select From Library. Upload your MP4 or MOV file, then wait a moment for the preview to load. Your video opens ready for subtitles and voice options.',
  },
  {
    number: 2,
    title: 'Step 2: Set Source and Target Languages',
    description:
      'Choose Japanese as the Source Language so FalcoCut can detect speech and on-screen text. Set English as your Target Language, or add a second target if needed. This creates multiple versions in one run.',
  },
  {
    number: 3,
    title: 'Step 3: Review Subtitles and Voice',
    description:
      'Turn on Translate Subtitles to translate Japanese subtitles to English and style them for your layout. Enable Translate Voice to generate an English AI voice, then pick a voice you like. Use Lip Sync for a more natural match on talking scenes.',
  },
  {
    number: 4,
    title: 'Step 4: Export and Share Your Results',
    description:
      'Preview the final video and choose Bilingual Subtitles or Translated Only for a clean look. When it is ready, open My Creations to download your file. Pick Download with Watermark for free, or upgrade for no watermark.',
  },
];

// Use Cases Section
const USE_CASES_CONTENT = {
  title: 'Unlock English Reach Across Real-World Japanese Content',
};

const USE_CASES: UseCaseItem[] = [
  {
    title: 'E-Commerce',
    description:
      'Turn Japanese product videos into clear English versions for more shoppers. Add English subtitles so buyers understand features, sizes, and care fast. Use English voice dubbing to reduce drop-offs on mobile. Keep your message consistent across listings, ads, and support pages.',
  },
  {
    title: 'Global Education',
    description:
      'Make Japanese lessons easier for English learners without rewriting your course. Add English subtitles so key terms and steps stay easy to follow. Use bilingual subtitles to support study and improve recall. English dubbing helps students focus when reading is hard.',
  },
  {
    title: 'Social Creators',
    description:
      'Reach English-speaking fans while keeping your Japanese style and timing. Add English subtitles for fast scrolling and better watch time. Dub into English so viewers can listen while commuting or multitasking. Lip sync makes talking clips feel natural and more shareable.',
  },
  {
    title: 'Business Marketing',
    description:
      'Localize Japanese promos for English markets without reshooting or hiring a studio. Translate Japanese subtitles to English to keep claims and offers clear. Add English voice dubbing for demos, explainers, and product launches. Lip sync improves trust when a spokesperson is on screen.',
  },
  {
    title: 'Event Collaboration',
    description:
      'Share Japanese talks and panels with English partners after the event ends. English subtitles help teams catch details, names, and action items. Dub into English for smoother playback during reviews and recaps. Better access speeds decisions across time zones and mixed-language groups.',
  },
  {
    title: 'Media & Podcasts',
    description:
      'Expand Japanese interviews into English clips that more listeners can enjoy. Add English subtitles for short highlights and silent autoplay feeds. Use English dubbing to keep long-form segments easy to follow. Lip sync helps video podcast cuts feel polished and professional.',
  },
];

// Language List Section
const LANGUAGE_LIST_CONTENT = {
  title: 'Cover More Markets with 35 Supported Languages',
  subtitle:
    'FalcoCut supports 35 languages, so you can reach new viewers without extra tools or editing skills. Upload your video, pick your languages, and get clear subtitles and natural voice options in minutes.',
  cta: 'Translate Japanese Video',
};

// Why Choose Section
const WHY_CHOOSE_CONTENT = {
  title: 'A Trusted Way to Turn Japanese Videos Into Clear English',
  subtitle:
    'FalcoCut helps you translate Japanese video to English without complex tools or long waits. Your workflow stays simple, fast, and reliable, so you can publish with confidence in every step.',
  cta: 'Translate Japanese Video to English Now',
};

const WHY_CHOOSE_REASONS: ReasonItem[] = [
  {
    title: 'Works Right in Your Browser',
    description:
      'FalcoCut runs in your browser, so you can start fast without installing anything. Upload your MP4 or MOV, and the preview shows up right away. The layout is simple, so beginners can finish a full job without stress. You stay focused on your video, not on learning new software.',
  },
  {
    title: 'Get English Results in Less Time',
    description:
      'FalcoCut is built to save time when you translate Japanese video to English. It handles speech and subtitle creation in one smooth flow. You can create up to two target languages in one run, which cuts repeat work. That means more uploads finished in a day, with fewer steps to manage.',
  },
  {
    title: 'More Control Over Subtitles and Voice',
    description:
      'Choose subtitles only, voice only, or both, based on what your audience needs. You can translate Japanese subtitles to English for clean captions or add an AI voice. Styling tools let you adjust font, size, color, and placement for better reading. These options help each video fit your channel and format.',
  },
  {
    title: 'Clearer English You Can Rely On',
    description:
      'FalcoCut aims for clear English that keeps the meaning of the original Japanese. It detects spoken words and can also recognize on-screen text for stronger subtitle output. You can review the preview and choose the subtitle mode that reads best. This helps your message stay accurate and easy to follow.',
  },
  {
    title: 'Flexible Pricing for Every Creator',
    description:
      'You can download a version with a watermark for free, which is great for testing. When you are ready, you can upgrade to download without a watermark. This keeps costs low while you check quality and timing first. It is a practical way to scale only when your content is ready.',
  },
  {
    title: 'Safer Handling for Your Video Files',
    description:
      'FalcoCut is designed to keep your process controlled and predictable from upload to download. You work inside one tool, which reduces the need to share files across many services. After processing, your results are easy to find in My Creations. That simple flow helps you stay organized and avoid mistakes.',
  },
];

// VS Section
const VS_CONTENT = {
  title: 'Human Translation vs. FalcoCut AI Workflow',
  subtitle:
    'Manual translation can take days and cost a lot. FalcoCut helps you translate Japanese video to English faster, at scale, with less work.',
  oldTitle: 'Manual Editing and Outsourcing',
  oldPoints: [
    'Slow back-and-forth reviews',
    'High cost per finished video',
    'Hard to keep terms consistent',
    'Many tools and file handoffs',
    'Long waits for revisions',
  ],
  oldConclusion: 'Manual work drags on and adds cost.',
  newTitle: 'FalcoCut Automation for Video Localization',
  newPoints: [
    'Fast subtitles and voice in one run',
    'Lower cost than hiring per project',
    'One workflow from upload to export',
    'Subtitles, dubbing, and lip sync options',
    'Supports 30+ languages for scaling',
  ],
  newConclusion: 'FalcoCut delivers clean results in minutes.',
  cta: 'Translate Japanese Video',
};

// FAQ Section
const FAQ_CONTENT = {
  title: 'Frequently Asked Questions',
};

const FAQS: FAQItem[] = [
  {
    question: 'How do I translate a Japanese video to English in FalcoCut?',
    answer:
      'Upload your MP4 or MOV, then set Japanese as the source language and English as the target language. Turn on Translate Subtitles, Translate Voice, or both, depending on what you need. When processing ends, open My Creations and download your English version with the subtitle style you chose.',
  },
  {
    question: 'Can I translate Japanese subtitles to English without dubbing the voice?',
    answer:
      'Yes, you can translate Japanese subtitles to English without changing the audio. Enable Translate Subtitles and keep Translate Voice turned off, then pick bilingual subtitles or translated-only subtitles. You can also adjust font, size, color, position, and background before you download.',
  },
  {
    question:
      'Does FalcoCut support English dubbing with lip sync for Japanese speakers?',
    answer:
      "Yes, Japanese and English are fully supported for voice translation and lip sync. Turn on Translate Voice, choose an English voice style, and enable Lip Sync for talking-head videos. This helps the new English narration match the speaker's mouth movement more naturally.",
  },
  {
    question: 'What video formats can I upload for Japanese-to-English translation?',
    answer:
      'FalcoCut supports MP4 and MOV uploads for video translation. Add your file using Add Video to Translate, or pick one from Select From Library. After upload, the preview appears automatically so you can confirm the content before processing.',
  },
  {
    question: 'How long does it take to convert a Japanese video into English?',
    answer:
      'Processing time depends on video length and which options you enable, such as subtitles, voice, and lip sync. Many videos finish quickly because everything runs in your browser with no setup. You can start the translation, then return to My Creations to download when it is ready.',
  },
  {
    question:
      'Can I create two English versions at once, like subtitles-only and dubbed?',
    answer:
      'You can choose up to two target languages in one run, which is useful when you need multiple translated outputs. For different English styles, run the same video again and change your settings, like subtitles-only versus voice plus lip sync. This keeps each output clean and matched to its purpose, such as learning or marketing.',
  },
  {
    question: 'How can I improve accuracy when translating Japanese audio to English?',
    answer:
      "Start by selecting Japanese as the source language so the system knows what it should detect. Use clear audio when possible, and choose a voice that matches the speaker's age and tone for better results. After subtitles are generated, review the text and adjust styling or display mode to fit your audience.",
  },
  {
    question: 'Can I edit the English subtitles after the Japanese video is processed?',
    answer:
      'Yes, FalcoCut gives you subtitle display choices and styling controls for the final video. You can switch between bilingual subtitles and translated-only subtitles, then fine-tune font, size, color, position, and background. This makes it easier to keep English captions readable on fast scenes or busy visuals.',
  },
  {
    question: 'Is there a free option, and what do I get with the paid plan?',
    answer:
      'FalcoCut offers a free download option that includes a watermark, so you can test Japanese-to-English results before paying. If you need a clean export, the paid option lets you download without a watermark. This setup is helpful for creators who want to validate quality first, then upgrade for final publishing.',
  },
  {
    question: 'Is my Japanese video kept secure when I upload it for translation?',
    answer:
      'FalcoCut handles your uploaded videos and generated results securely during processing and download. You can work in the browser, which reduces setup risk and keeps the workflow simple. If you are translating private content, download your results promptly from My Creations and manage sharing carefully on your side.',
  },
];

// Footer CTA Section
const FOOTER_CTA_CONTENT = {
  title: 'Convert Japanese Clips to English Fast Today',
  subtitle:
    'Use FalcoCut to translate japanese video to english by uploading your MP4 or MOV, then generate English voice and subtitles with lip sync and bilingual subtitle options in minutes.',
  cta: 'Translate Japanese Video',
};

// SEO Config
const SEO_CONFIG: SEOHeadProps = {
  title: 'Translate Japanese Video to English Instantly Online',
  description:
    'Translate japanese video to english with FalcoCut in minutes. Convert Japanese speech and translate japanese subtitles to english with clear results, fast.',
  url: PAGE_URL,
  image: 'https://falcocut.ai/images/og-img.png',
  imageAlt: 'FalcoCut Video Translator - Japanese to English',
  publishedTime: '2026-02-06',
  modifiedTime: '2026-02-06',
  preloadImages: [FEATURE_IMAGES[0]],
  structuredData: [
    createSoftwareAppSchema({
      name: 'FalcoCut Video Translator - Japanese to English',
      description:
        'Translate japanese video to english with FalcoCut in minutes. Convert Japanese speech and translate japanese subtitles to english with clear results, fast.',
      url: PAGE_URL,
      applicationCategory: 'MultimediaApplication',
      ratingValue: '4.9',
      ratingCount: '2350',
      featureList: [
        'AI Video Translation',
        'Voice Cloning & Dubbing',
        'Auto Subtitles',
        'Lip Sync',
        'Multi-language Support',
      ],
    }),
    createOrganizationSchema({
      name: 'FalcoCut',
      url: 'https://falcocut.ai',
      logo: 'https://falcocut.ai/images/logo.png',
    }),
    createFAQSchema({
      faqs: FAQS.filter((faq) => faq.answer).map((faq) => ({
        question: faq.question,
        answer: faq.answer as string,
      })),
    }),
    createBreadcrumbSchema({
      items: [
        { name: 'Home', url: 'https://falcocut.ai' },
        { name: 'Video Translator', url: 'https://falcocut.ai/video-translator' },
        { name: 'Japanese to English', url: PAGE_URL },
      ],
    }),
  ],
};

// ============ Page Component ============

const JapaneseToEnglishPage: React.FC = () => {
  return (
    <>
      <SEOHead {...SEO_CONFIG} />

      <HeroSection
        title={HERO_CONTENT.title}
        subtitle={HERO_CONTENT.subtitle}
        ctaText={HERO_CONTENT.cta}
        sourceLanguageLabel="Source Language"
        sourceLanguageDefault={HERO_CONTENT.sourceLanguage}
        targetLanguageLabel="Target Language"
        targetLanguageDefault={HERO_CONTENT.targetLanguage}
      />

      <VideoTranslationDemos
        title={DEMOS_CONTENT.title}
        subtitle={DEMOS_CONTENT.subtitle}
        ctaText={DEMOS_CONTENT.cta}
      />

      <FeaturesSection
        title={FEATURES_CONTENT.title}
        subtitle={FEATURES_CONTENT.subtitle}
        features={FEATURES}
        ctaText={FEATURES_CONTENT.cta}
      />

      <HowToSection
        title={HOW_TO_CONTENT.title}
        steps={HOW_TO_STEPS}
        ctaText={HOW_TO_CONTENT.cta}
      />

      <UseCasesSection title={USE_CASES_CONTENT.title} useCases={USE_CASES} />

      <LanguageListSection
        title={LANGUAGE_LIST_CONTENT.title}
        subtitle={LANGUAGE_LIST_CONTENT.subtitle}
        ctaText={LANGUAGE_LIST_CONTENT.cta}
      />

      <WhyChooseSection
        title={WHY_CHOOSE_CONTENT.title}
        subtitle={WHY_CHOOSE_CONTENT.subtitle}
        reasons={WHY_CHOOSE_REASONS}
        ctaText={WHY_CHOOSE_CONTENT.cta}
      />

      <VsSection
        title={VS_CONTENT.title}
        subtitle={VS_CONTENT.subtitle}
        oldTitle={VS_CONTENT.oldTitle}
        oldPoints={VS_CONTENT.oldPoints}
        oldConclusion={VS_CONTENT.oldConclusion}
        newTitle={VS_CONTENT.newTitle}
        newPoints={VS_CONTENT.newPoints}
        newConclusion={VS_CONTENT.newConclusion}
        ctaText={VS_CONTENT.cta}
      />

      <ExploreToolsSection />

      <FAQSection title={FAQ_CONTENT.title} faqs={FAQS} />

      <FooterCTASection
        title={FOOTER_CTA_CONTENT.title}
        subtitle={FOOTER_CTA_CONTENT.subtitle}
        ctaText={FOOTER_CTA_CONTENT.cta}
      />
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
    revalidate: 3600,
  };
};

export default withLandingLayout(JapaneseToEnglishPage);
